import { makeAutoObservable, observable, ObservableSet, values } from 'mobx'
import { DocStore } from 'stores/DocStore'
import { ImageNodeAttrs } from 'common/Prose/nodeViews/Image/Types'
import { createUseStore } from 'stores/util'
import { ArtisticFilterType, ImageEffect, stringOrNumber } from '@cloudinary/url-gen/types/types'
import { CloudinaryImage } from '@cloudinary/url-gen'

interface IImageVariant {
  image: CloudinaryImage
  url: string
  sourceUrl: string
}

export interface ArtisticFilterVariant extends IImageVariant {
  action: 'artisticFilter'
  qualifier: ArtisticFilterType
}

export interface ImageEffectEffectVariant<
  Effect extends ImageEffect = ImageEffect,
  Qualifier extends stringOrNumber = stringOrNumber,
> extends IImageVariant {
  action: Effect
  qualifier?: Qualifier
}

export type ImageVariant = ArtisticFilterVariant | ImageEffectEffectVariant

export class ImageStore {
  docStore: DocStore
  attrs: ImageNodeAttrs
  private _variants: ObservableSet<ImageVariant> = observable.set([])

  constructor(docStore: DocStore, attrs: ImageNodeAttrs) {
    makeAutoObservable(this)
    this.docStore = docStore
    this.docStore.addImageStore(attrs.src, this)
    this.attrs = attrs
  }

  createVariant = (variant: Omit<ImageVariant, 'sourceUrl'>) => {
    this._variants.add({ ...variant, sourceUrl: this.attrs.src })
  }

  get variants() {
    return values(this._variants)
  }

  get lastVariant() {
    if (this.variants.length < 1) return null

    return this.variants[this.variants.length - 1]
  }

  clearVariants = () => {
    this._variants.clear()
  }

  destroy() {
    if (this.docStore.imageStores.has(this.attrs.src)) {
      this.docStore.imageStores.delete(this.attrs.src)
    }
  }
}

export const [ImageStoreContext, useImageStore] = createUseStore(ImageStore)
